import React, { useState } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { format, subDays, addDays, startOfWeek, endOfWeek } from 'date-fns'
import { es } from 'date-fns/locale'
import { supabase } from '../../lib/supabase'
import Toast from '../ui/Toast'
import ConfirmationModal from '../ui/ConfirmationModal'

const { FiCopy, FiCalendar, FiCheck, FiAlertCircle, FiRepeat, FiAlertTriangle } = FiIcons

const MenuCloner = () => {
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState(null)
  const [targetDate, setTargetDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [lastMenuDate, setLastMenuDate] = useState(format(subDays(new Date(), 1), 'yyyy-MM-dd'))
  const [confirmationModal, setConfirmationModal] = useState(null)
  const [duplicateCheck, setDuplicateCheck] = useState(null)

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  // Función para verificar duplicados
  const checkForDuplicates = async (sourceDate, targetDate) => {
    try {
      // Obtener platos de la fecha origen
      const { data: sourcePlatos, error: sourceError } = await supabase
        .from('platos')
        .select('nombre, categoria')
        .eq('fecha', sourceDate)
        .eq('visible', true)

      if (sourceError) {
        console.error('Error fetching source platos:', sourceError)
        return { error: 'Error al verificar platos de origen' }
      }

      if (!sourcePlatos || sourcePlatos.length === 0) {
        return { error: 'No hay platos en la fecha de origen' }
      }

      // Obtener platos existentes en la fecha destino
      const { data: targetPlatos, error: targetError } = await supabase
        .from('platos')
        .select('nombre, categoria')
        .eq('fecha', targetDate)
        .eq('visible', true)

      if (targetError) {
        console.error('Error fetching target platos:', targetError)
        return { error: 'Error al verificar platos de destino' }
      }

      // Verificar duplicados
      const duplicates = []
      if (targetPlatos && targetPlatos.length > 0) {
        sourcePlatos.forEach(sourcePlato => {
          const isDuplicate = targetPlatos.some(targetPlato => 
            targetPlato.nombre.toLowerCase() === sourcePlato.nombre.toLowerCase() &&
            targetPlato.categoria === sourcePlato.categoria
          )
          if (isDuplicate) {
            duplicates.push(sourcePlato)
          }
        })
      }

      return {
        sourcePlatos,
        targetPlatos: targetPlatos || [],
        duplicates,
        hasExistingPlatos: targetPlatos && targetPlatos.length > 0,
        hasDuplicates: duplicates.length > 0
      }
    } catch (error) {
      console.error('Error checking duplicates:', error)
      return { error: 'Error al verificar duplicados' }
    }
  }

  // Función para verificar duplicados semanales (LUNES A DOMINGO)
  const checkWeeklyDuplicates = async () => {
    try {
      const today = new Date()
      const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 }) // Lunes
      const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 })     // Domingo
      const startOfLastWeek = subDays(startOfCurrentWeek, 7)
      const endOfLastWeek = subDays(endOfCurrentWeek, 7)

      // Obtener menús de la semana pasada (LUNES A DOMINGO)
      const { data: lastWeekMenus, error: lastWeekError } = await supabase
        .from('platos')
        .select('fecha, nombre, categoria')
        .gte('fecha', format(startOfLastWeek, 'yyyy-MM-dd'))
        .lte('fecha', format(endOfLastWeek, 'yyyy-MM-dd'))
        .eq('visible', true)

      if (lastWeekError) {
        return { error: 'Error al obtener menús de la semana pasada' }
      }

      if (!lastWeekMenus || lastWeekMenus.length === 0) {
        return { error: 'No hay menús en la semana pasada' }
      }

      // Obtener menús de esta semana (LUNES A DOMINGO)
      const { data: currentWeekMenus, error: currentWeekError } = await supabase
        .from('platos')
        .select('fecha, nombre, categoria')
        .gte('fecha', format(startOfCurrentWeek, 'yyyy-MM-dd'))
        .lte('fecha', format(endOfCurrentWeek, 'yyyy-MM-dd'))
        .eq('visible', true)

      if (currentWeekError) {
        return { error: 'Error al obtener menús de esta semana' }
      }

      // Agrupar por fecha
      const lastWeekByDate = lastWeekMenus.reduce((acc, plato) => {
        if (!acc[plato.fecha]) acc[plato.fecha] = []
        acc[plato.fecha].push(plato)
        return acc
      }, {})

      const currentWeekByDate = (currentWeekMenus || []).reduce((acc, plato) => {
        if (!acc[plato.fecha]) acc[plato.fecha] = []
        acc[plato.fecha].push(plato)
        return acc
      }, {})

      // Calcular duplicados por día (TODOS LOS DÍAS DE LA SEMANA)
      const duplicatesByDay = {}
      let totalDuplicates = 0
      let totalSourcePlatos = 0

      Object.entries(lastWeekByDate).forEach(([sourceDate, sourcePlatos]) => {
        const sourceDateObj = new Date(sourceDate)
        const dayOfWeek = sourceDateObj.getDay()
        
        // Mapear todos los días de la semana (0=Domingo, 1=Lunes, ..., 6=Sábado)
        let daysFromMonday
        if (dayOfWeek === 0) { // Domingo
          daysFromMonday = 6
        } else { // Lunes a Sábado
          daysFromMonday = dayOfWeek - 1
        }
        
        const targetDate = addDays(startOfCurrentWeek, daysFromMonday)
        const targetDateString = format(targetDate, 'yyyy-MM-dd')
        
        const targetPlatos = currentWeekByDate[targetDateString] || []
        const dayDuplicates = []

        sourcePlatos.forEach(sourcePlato => {
          const isDuplicate = targetPlatos.some(targetPlato => 
            targetPlato.nombre.toLowerCase() === sourcePlato.nombre.toLowerCase() &&
            targetPlato.categoria === sourcePlato.categoria
          )
          if (isDuplicate) {
            dayDuplicates.push(sourcePlato)
          }
        })

        // Usar nombre del día en español
        const dayName = format(targetDate, "EEEE", { locale: es })
        
        duplicatesByDay[dayName] = {
          sourceDate,
          targetDate: targetDateString,
          sourcePlatos: sourcePlatos.length,
          targetPlatos: targetPlatos.length,
          duplicates: dayDuplicates.length
        }

        totalDuplicates += dayDuplicates.length
        totalSourcePlatos += sourcePlatos.length
      })

      return {
        duplicatesByDay,
        totalDuplicates,
        totalSourcePlatos,
        hasExistingPlatos: Object.values(currentWeekByDate).some(day => day.length > 0),
        hasDuplicates: totalDuplicates > 0
      }
    } catch (error) {
      console.error('Error checking weekly duplicates:', error)
      return { error: 'Error al verificar duplicados semanales' }
    }
  }

  const handleCloneMenu = async (skipConfirmation = false) => {
    try {
      if (!skipConfirmation) {
        setLoading(true)
        const duplicateInfo = await checkForDuplicates(lastMenuDate, targetDate)
        setLoading(false)

        if (duplicateInfo.error) {
          showToast(duplicateInfo.error, 'error')
          return
        }

        // Mostrar modal de confirmación
        setConfirmationModal({
          type: 'single',
          title: 'Confirmar Clonación de Menú',
          sourceDate: lastMenuDate,
          targetDate: targetDate,
          sourcePlatos: duplicateInfo.sourcePlatos.length,
          targetPlatos: duplicateInfo.targetPlatos.length,
          duplicates: duplicateInfo.duplicates.length,
          hasExistingPlatos: duplicateInfo.hasExistingPlatos,
          hasDuplicates: duplicateInfo.hasDuplicates,
          duplicatesList: duplicateInfo.duplicates,
          onConfirm: () => {
            setConfirmationModal(null)
            handleCloneMenu(true)
          },
          onCancel: () => setConfirmationModal(null)
        })
        return
      }

      setLoading(true)

      // Proceder con la clonación
      const { data: sourceMenu, error: fetchError } = await supabase
        .from('platos')
        .select('*')
        .eq('fecha', lastMenuDate)
        .eq('visible', true)

      if (fetchError) {
        console.error('Error fetching source menu:', fetchError)
        showToast('Error al obtener el menú de origen', 'error')
        return
      }

      if (!sourceMenu || sourceMenu.length === 0) {
        showToast('No hay platos en la fecha de origen seleccionada', 'error')
        return
      }

      const platosToClone = sourceMenu.map(plato => {
        const { id, created_at, updated_at, ...platoData } = plato
        return {
          ...platoData,
          fecha: targetDate
        }
      })

      const { error: insertError } = await supabase
        .from('platos')
        .insert(platosToClone)

      if (insertError) {
        console.error('Error cloning menu:', insertError)
        showToast('Error al clonar el menú: ' + insertError.message, 'error')
        return
      }

      const platosClonados = platosToClone.length
      showToast(`Se han clonado ${platosClonados} platos del ${format(new Date(lastMenuDate), "d 'de' MMMM", { locale: es })} al ${format(new Date(targetDate), "d 'de' MMMM", { locale: es })}.`)
    } catch (error) {
      console.error('Error cloning menu:', error)
      showToast('Error al clonar el menú. Por favor, inténtalo de nuevo.', 'error')
    } finally {
      setLoading(false)
    }
  }

  const handleCloneYesterday = async () => {
    const yesterday = format(subDays(new Date(), 1), 'yyyy-MM-dd')
    const today = format(new Date(), 'yyyy-MM-dd')
    
    setLastMenuDate(yesterday)
    setTargetDate(today)
    
    // Verificar duplicados y mostrar confirmación
    setLoading(true)
    const duplicateInfo = await checkForDuplicates(yesterday, today)
    setLoading(false)

    if (duplicateInfo.error) {
      showToast(duplicateInfo.error, 'error')
      return
    }

    setConfirmationModal({
      type: 'yesterday',
      title: 'Confirmar Clonación Rápida',
      sourceDate: yesterday,
      targetDate: today,
      sourcePlatos: duplicateInfo.sourcePlatos.length,
      targetPlatos: duplicateInfo.targetPlatos.length,
      duplicates: duplicateInfo.duplicates.length,
      hasExistingPlatos: duplicateInfo.hasExistingPlatos,
      hasDuplicates: duplicateInfo.hasDuplicates,
      duplicatesList: duplicateInfo.duplicates,
      onConfirm: () => {
        setConfirmationModal(null)
        handleCloneMenu(true)
      },
      onCancel: () => setConfirmationModal(null)
    })
  }

  const handleCloneWeeklyMenu = async (skipConfirmation = false) => {
    try {
      if (!skipConfirmation) {
        setLoading(true)
        const duplicateInfo = await checkWeeklyDuplicates()
        setLoading(false)

        if (duplicateInfo.error) {
          showToast(duplicateInfo.error, 'error')
          return
        }

        // Mostrar modal de confirmación
        setConfirmationModal({
          type: 'weekly',
          title: 'Confirmar Clonación Semanal',
          weeklyData: duplicateInfo,
          onConfirm: () => {
            setConfirmationModal(null)
            handleCloneWeeklyMenu(true)
          },
          onCancel: () => setConfirmationModal(null)
        })
        return
      }

      setLoading(true)

      const today = new Date()
      const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 }) // Lunes
      const endOfCurrentWeek = endOfWeek(today, { weekStartsOn: 1 })     // Domingo
      const startOfLastWeek = subDays(startOfCurrentWeek, 7)
      const endOfLastWeek = subDays(endOfCurrentWeek, 7)

      // Obtener menús de la semana pasada (LUNES A DOMINGO)
      const { data: lastWeekMenus, error: fetchError } = await supabase
        .from('platos')
        .select('*')
        .gte('fecha', format(startOfLastWeek, 'yyyy-MM-dd'))
        .lte('fecha', format(endOfLastWeek, 'yyyy-MM-dd'))
        .eq('visible', true)

      if (fetchError) {
        console.error('Error fetching last week menus:', fetchError)
        showToast('Error al obtener los menús de la semana pasada', 'error')
        return
      }

      if (!lastWeekMenus || lastWeekMenus.length === 0) {
        showToast('No hay menús en la semana pasada para clonar', 'error')
        return
      }

      // Agrupar menús por fecha
      const menusByDate = lastWeekMenus.reduce((acc, plato) => {
        if (!acc[plato.fecha]) {
          acc[plato.fecha] = []
        }
        acc[plato.fecha].push(plato)
        return acc
      }, {})

      let totalPlatosCloned = 0

      // Procesar TODOS los días de la semana (Lunes a Domingo)
      for (const [sourceDate, platos] of Object.entries(menusByDate)) {
        const sourceDateObj = new Date(sourceDate)
        const dayOfWeek = sourceDateObj.getDay()
        
        // Mapear día de la semana a offset desde el lunes
        let daysFromMonday
        if (dayOfWeek === 0) { // Domingo
          daysFromMonday = 6
        } else { // Lunes a Sábado
          daysFromMonday = dayOfWeek - 1
        }
        
        const targetDate = addDays(startOfCurrentWeek, daysFromMonday)
        const targetDateString = format(targetDate, 'yyyy-MM-dd')

        const platosToClone = platos.map(plato => {
          const { id, created_at, updated_at, ...platoData } = plato
          return {
            ...platoData,
            fecha: targetDateString
          }
        })

        const { error: insertError } = await supabase
          .from('platos')
          .insert(platosToClone)

        if (insertError) {
          console.error(`Error cloning menu for ${targetDateString}:`, insertError)
          showToast(`Error al clonar el menú del ${format(targetDate, "EEEE", { locale: es })}: ${insertError.message}`, 'error')
          continue
        }

        totalPlatosCloned += platosToClone.length
      }

      if (totalPlatosCloned > 0) {
        showToast(`¡Éxito! Se han clonado ${totalPlatosCloned} platos de la semana pasada a esta semana (Lunes a Domingo).`)
      } else {
        showToast('No se pudieron clonar menús de la semana pasada', 'error')
      }
    } catch (error) {
      console.error('Error cloning weekly menu:', error)
      showToast('Error al clonar el menú semanal. Por favor, inténtalo de nuevo.', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      {/* Confirmation Modal */}
      {confirmationModal && (
        <ConfirmationModal
          type={confirmationModal.type}
          title={confirmationModal.title}
          sourceDate={confirmationModal.sourceDate}
          targetDate={confirmationModal.targetDate}
          sourcePlatos={confirmationModal.sourcePlatos}
          targetPlatos={confirmationModal.targetPlatos}
          duplicates={confirmationModal.duplicates}
          hasExistingPlatos={confirmationModal.hasExistingPlatos}
          hasDuplicates={confirmationModal.hasDuplicates}
          duplicatesList={confirmationModal.duplicatesList}
          weeklyData={confirmationModal.weeklyData}
          onConfirm={confirmationModal.onConfirm}
          onCancel={confirmationModal.onCancel}
        />
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-vip-orange">Clonar Menú</h1>
        <p className="text-vip-brown">Copia platos de una fecha a otra o clona menús semanales</p>
      </div>

      {/* Quick Clone Yesterday */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-vip-orange to-vip-dark rounded-lg p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-2">Acción Rápida</h3>
            <p className="text-orange-100">
              Copia todos los platos de ayer a hoy con verificación de duplicados
            </p>
          </div>
          <button
            onClick={handleCloneYesterday}
            disabled={loading}
            className="flex items-center px-6 py-3 bg-white text-vip-orange rounded-md hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50 font-semibold"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-vip-orange mr-2"></div>
                Verificando...
              </>
            ) : (
              <>
                <SafeIcon icon={FiCopy} className="mr-2" />
                Clonar Menú de Ayer
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Weekly Clone */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.05 }}
        className="bg-gradient-to-r from-blue-500 to-blue-700 rounded-lg p-6 text-white"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold mb-2">Clonación Semanal Completa</h3>
            <p className="text-blue-100">
              Copia todos los menús de la semana pasada (Lunes a Domingo) con verificación de duplicados
            </p>
          </div>
          <button
            onClick={() => handleCloneWeeklyMenu(false)}
            disabled={loading}
            className="flex items-center px-6 py-3 bg-white text-blue-700 rounded-md hover:bg-gray-100 transition-colors duration-200 disabled:opacity-50 font-semibold"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-2"></div>
                Verificando...
              </>
            ) : (
              <>
                <SafeIcon icon={FiRepeat} className="mr-2" />
                Clonar Menú Semanal
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Custom Clone */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-white rounded-lg shadow-md p-6"
      >
        <h3 className="text-lg font-semibold text-vip-brown mb-4">
          Clonación Personalizada
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label className="block text-sm font-medium text-vip-brown mb-2">
              <SafeIcon icon={FiCalendar} className="inline mr-2" />
              Fecha de Origen
            </label>
            <input
              type="date"
              value={lastMenuDate}
              onChange={(e) => setLastMenuDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
            />
            <p className="mt-1 text-xs text-gray-500">
              Fecha del menú que quieres copiar
            </p>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-vip-brown mb-2">
              <SafeIcon icon={FiCalendar} className="inline mr-2" />
              Fecha de Destino
            </label>
            <input
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
            />
            <p className="mt-1 text-xs text-gray-500">
              Fecha donde se copiará el menú
            </p>
          </div>
        </div>

        <div className="flex justify-center">
          <button
            onClick={() => handleCloneMenu(false)}
            disabled={loading || !lastMenuDate || !targetDate}
            className="flex items-center px-6 py-3 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Verificando...
              </>
            ) : (
              <>
                <SafeIcon icon={FiCopy} className="mr-2" />
                Clonar Menú
              </>
            )}
          </button>
        </div>
      </motion.div>

      {/* Instructions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-6"
      >
        <h4 className="font-semibold text-blue-900 mb-3">¿Cómo funciona la nueva protección?</h4>
        <ul className="space-y-2 text-blue-800 text-sm">
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            <strong>Verificación de Duplicados:</strong> Antes de clonar, el sistema verifica si ya existen platos similares en la fecha de destino
          </li>
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            <strong>Modal de Confirmación:</strong> Te muestra exactamente qué se va a clonar y si hay conflictos potenciales
          </li>
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            <strong>Advertencias Visuales:</strong> Los duplicados se destacan con colores de advertencia para que puedas decidir
          </li>
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            <strong>Clonación Inteligente:</strong> Puedes proceder incluso con duplicados si es necesario
          </li>
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            <strong>Clonación Semanal Completa:</strong> Ahora clona de Lunes a Domingo (semana completa) y verifica duplicados día por día
          </li>
        </ul>
      </motion.div>
    </div>
  )
}

export default MenuCloner